import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   * @param {Event} [event] - The change event, when an input calls this directly
   */
  updateFilters(event) {
    // Single-choice groups (the pack-size pills): ticking one clears the rest.
    // Those inputs carry their own on:change, so the event target is the input.
    const input = event?.target;
    if (this.hasAttribute('data-single-select') && input instanceof HTMLInputElement && input.checked) {
      for (const other of this.refs.facetInputs ?? []) {
        if (other !== input) other.checked = false;
      }
    }

    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input (hidden; submitted with the form)
 * @property {HTMLInputElement} maxInput - The maximum price input (hidden; submitted with the form)
 * @property {HTMLElement} [slider] - The slider wrapper that paints the selected part of the track
 * @property {HTMLInputElement} [minRange] - The low-price thumb
 * @property {HTMLInputElement} [maxRange] - The high-price thumb
 * @property {HTMLElement} [minLabel] - Shows the low price
 * @property {HTMLElement} [maxLabel] - Shows the high price
 */

/**
 * Lowest and highest price per pack size, keyed by the price-bounds URL so
 * the sidebar and drawer sliders share one request.
 * @type {Map<string, Promise<Map<string, {lo: number, hi: number}>>>}
 */
const sizePriceBounds = new Map();

/**
 * Loads templates/collection.price-bounds.liquid ([size, price in minor
 * units] pairs) and reduces it to each size's price range in major units.
 * @param {string} url - The collection's ?view=price-bounds URL
 * @param {number} unit - Minor units per major unit
 * @returns {Promise<Map<string, {lo: number, hi: number}>>}
 */
function loadSizePriceBounds(url, unit) {
  let request = sizePriceBounds.get(url);
  if (request) return request;

  request = fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`Price bounds request failed: ${response.status}`);
      return response.json();
    })
    .then((/** @type {[string, number][]} */ pairs) => {
      /** @type {Map<string, {lo: number, hi: number}>} */
      const bounds = new Map();
      for (const [size, minor] of pairs) {
        const price = minor / unit;
        const range = bounds.get(size);
        if (range) {
          range.lo = Math.min(range.lo, price);
          range.hi = Math.max(range.hi, price);
        } else {
          bounds.set(size, { lo: price, hi: price });
        }
      }
      return bounds;
    });
  // A failed request can be retried by the next slider that asks.
  request.catch(() => sizePriceBounds.delete(url));
  sizePriceBounds.set(url, request);
  return request;
}

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;
  /** Full money format without decimals, for the slider labels. @type {string} */
  #labelFormat = '{{amount}}';

  /** Price range per pack size, once loaded (only when the Size filter is on). @type {Map<string, {lo: number, hi: number}> | null} */
  #sizeBounds = null;

  /**
   * Re-places the thumbs after a section re-render. Morph resets the thumbs
   * and labels to the server's markup (the ends and the overall range, since
   * Liquid can't work out log-scale positions). The hidden inputs are left
   * out: they change on every drag. The component's own writes are dropped
   * with takeRecords(), so it never reacts to itself.
   */
  #morphObserver = new MutationObserver((records) => {
    if (records.some((record) => !(record.target instanceof HTMLInputElement && record.target.type === 'hidden'))) {
      this.#syncSlider();
    }
  });

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    this.#morphObserver.observe(this, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['value'],
    });
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');
    this.#labelFormat = (this.dataset.moneyFormat ?? '{{amount}}')
      .replace(/{{\s*amount\s*}}/, '{{amount_no_decimals}}')
      .replace(/{{\s*amount_with_comma_separator\s*}}/, '{{amount_no_decimals_with_comma_separator}}')
      .replace(/{{\s*amount_with_space_separator\s*}}/, '{{amount_no_decimals_with_space_separator}}');
    // Start loading the pack prices first, so the first paint waits for them.
    this.#loadSizeBounds();
    this.#syncSlider();
  }

  /**
   * With the Size filter on, fetches each pack size's price range so the
   * slider spans the ticked size (₹260–26,565 for 25 g rather than ₹0 to the
   * dearest 5 Kg pack). On failure the slider keeps the overall range.
   */
  async #loadSizeBounds() {
    const { boundsUrl } = this.dataset;
    if (!boundsUrl) return;

    this.#boundsPending = true;
    try {
      this.#sizeBounds = await loadSizePriceBounds(boundsUrl, this.#unit);
    } catch {
      // Fall back to the overall range rather than leaving the labels blank.
    }
    this.#boundsPending = false;
    if (this.isConnected) this.#syncSlider();
  }

  /** Waiting for the pack prices: hold the labels so the 5 Kg ceiling never flashes. */
  #boundsPending = false;

  /**
   * The Size filter's checkboxes in this slider's own form (sidebar or drawer).
   * @returns {HTMLInputElement[]}
   */
  #sizeBoxes() {
    const { sizeParam } = this.dataset;
    const form = this.closest('form');
    if (!sizeParam || !form) return [];
    return [...form.querySelectorAll(`input[type="checkbox"][name="${CSS.escape(sizeParam)}"]`)].filter(
      (input) => input instanceof HTMLInputElement
    );
  }

  /**
   * The smallest pack (lowest top price, i.e. 25 g): what a price range means
   * when no size is ticked.
   * @returns {string | null}
   */
  #defaultSize() {
    if (!this.#sizeBounds) return null;
    const offered = new Set(this.#sizeBoxes().map((box) => box.value));
    let best = null;
    for (const [size, range] of this.#sizeBounds) {
      if (offered.size && !offered.has(size)) continue;
      if (!best || range.hi < best.hi) best = { size, hi: range.hi };
    }
    return best?.size ?? null;
  }

  /**
   * The price span the slider covers, in major units: the ticked size's
   * lowest to highest price, or the smallest pack's (25 g) when none is
   * ticked — price filtering is per pack (see snippets/pack-price-in-range).
   * Falls back to 0 to the collection's highest price without the data.
   * @returns {{lo: number, hi: number}}
   */
  get #range() {
    const overall = { lo: 0, hi: this.#maxPrice };
    if (!this.#sizeBounds) return overall;

    const boxes = this.#sizeBoxes();
    const ticked = boxes.filter((box) => box.checked).map((box) => box.value);
    const sizes = ticked.length ? ticked : [this.#defaultSize()];
    let lo = Infinity;
    let hi = 0;
    for (const size of sizes) {
      const range = size ? this.#sizeBounds.get(size) : undefined;
      if (!range) continue;
      lo = Math.min(lo, range.lo);
      hi = Math.max(hi, range.hi);
    }
    return hi > 0 ? { lo: lo === Infinity ? 0 : lo, hi } : overall;
  }

  /**
   * A price range means the price of a pack someone can buy. Shopify matches
   * it against every variant, so on its own a range also lets in packs of
   * the wrong size (a ₹2,400 floor admits products whose 1 Kg costs that) and
   * sold-out packs (a ₹12,000 floor admits cheap materials through their
   * sold-out 5 Kg). So when a range is first set, tick "In stock" and, with
   * the Size filter on, the smallest pack — visibly, in their own filters, and
   * only if nothing there is ticked. Only on first set, so un-ticking sticks.
   */
  #applyPriceDefaults() {
    const { minInput, maxInput } = this.refs;
    if (!minInput.value && !maxInput.value) return;

    const params = new URL(window.location.href).searchParams;
    if (params.has(minInput.name) || params.has(maxInput.name)) return;

    const form = this.closest('form');
    const inStock = form?.querySelector('input[type="checkbox"][name="filter.v.availability"][value="1"]');
    const availability = form?.querySelectorAll('input[type="checkbox"][name="filter.v.availability"]') ?? [];
    if (inStock instanceof HTMLInputElement && ![...availability].some((box) => box instanceof HTMLInputElement && box.checked)) {
      inStock.checked = true;
    }

    const boxes = this.#sizeBoxes();
    if (!boxes.length || boxes.some((box) => box.checked)) return;
    const size = this.#defaultSize();
    const box = boxes.find((input) => input.value === size);
    if (box) box.checked = true;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncSlider();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    this.#morphObserver.disconnect();
  }

  /**
   * Moves the thumbs to the submitted values whenever filters change, so a
   * clear (which empties every input, thumbs included) resets them to the ends.
   */
  #onFilterUpdate = () => this.#syncSlider();

  /**
   * Minor units per major unit for the currency (100 for INR, 1 for JPY).
   * @returns {number}
   */
  get #unit() {
    return convertMoneyToMinorUnits('1', this.currency) ?? 100;
  }

  /**
   * The highest price, in major units.
   * @returns {number}
   */
  get #maxPrice() {
    return Number(this.dataset.rangeMax ?? 0) / this.#unit;
  }

  /**
   * Prices span orders of magnitude (₹260 samples to multi-lakh 5 Kg packs,
   * and even one size runs 100×), so a linear track would crowd most
   * products into its first few pixels. The thumbs move on a log scale from
   * the range's lowest price (or a floor near the bottom when that's 0) to
   * its highest; the ends map exactly to those, and mean "no limit".
   * @param {number} position - Thumb position, 0…steps
   * @param {number} steps - The range input's max
   * @returns {number} Price in major units
   */
  #positionToPrice(position, steps) {
    const { lo, hi } = this.#range;
    if (position <= 0) return lo;
    if (position >= steps) return hi;

    const floor = lo > 0 ? lo : Math.max(1, hi / 20000);
    const raw = hi > floor ? floor * Math.pow(hi / floor, position / steps) : lo + (position / steps) * (hi - lo);

    // Two significant figures, so labels read as 1,400 rather than 1,407.
    const magnitude = raw < 10 ? 1 : Math.pow(10, Math.floor(Math.log10(raw)) - 1);
    return Math.min(hi, Math.max(lo, Math.round(raw / magnitude) * magnitude));
  }

  /**
   * @param {number} price - Price in major units
   * @param {number} steps - The range input's max
   * @returns {number} Thumb position, 0…steps
   */
  #priceToPosition(price, steps) {
    const { lo, hi } = this.#range;
    if (price <= lo) return 0;
    if (price >= hi) return steps;

    const floor = lo > 0 ? lo : Math.max(1, hi / 20000);
    const ratio =
      hi > floor ? Math.log(Math.max(price, floor) / floor) / Math.log(hi / floor) : (price - lo) / (hi - lo);
    return Math.min(steps, Math.max(0, Math.round(ratio * steps)));
  }

  /**
   * Reads a hidden input's value ("1,976,645.00" from Liquid, "1400" from the slider) as major units.
   * @param {HTMLInputElement} input
   * @returns {number | null}
   */
  #inputPrice(input) {
    const minor = convertMoneyToMinorUnits(input.value, this.currency);
    return minor === null ? null : minor / this.#unit;
  }

  /**
   * Places the thumbs from the hidden inputs' values.
   */
  #syncSlider() {
    const { minRange, maxRange, minInput, maxInput } = this.refs;
    if (!minRange || !maxRange || !minInput || !maxInput || this.#boundsPending) return;

    const steps = Number(maxRange.max) || 1000;
    const { lo, hi } = this.#range;
    const low = this.#inputPrice(minInput);
    const high = this.#inputPrice(maxInput);
    minRange.value = String(low === null ? 0 : this.#priceToPosition(low, steps));
    maxRange.value = String(high === null ? steps : this.#priceToPosition(high, steps));
    this.#paintSlider(low ?? lo, high ?? hi);
  }

  /**
   * Fills the track between the thumbs and updates the price labels.
   * @param {number} low - Low price in major units
   * @param {number} high - High price in major units
   */
  #paintSlider(low, high) {
    const { slider, minRange, maxRange, minLabel, maxLabel } = this.refs;
    if (!minRange || !maxRange) return;

    const steps = Number(maxRange.max) || 1000;
    const from = Number(minRange.value);
    const to = Number(maxRange.value);
    const lowText = formatMoney(Math.round(low) * this.#unit, this.#labelFormat, this.currency);
    const highText = formatMoney(Math.round(high) * this.#unit, this.#labelFormat, this.currency);

    if (slider) {
      slider.style.setProperty('--price-from', `${(from / steps) * 100}%`);
      slider.style.setProperty('--price-to', `${(to / steps) * 100}%`);
      slider.toggleAttribute('data-min-on-top', from > steps / 2);
    }
    if (minLabel && minLabel.textContent !== lowText) minLabel.textContent = lowText;
    if (maxLabel && maxLabel.textContent !== highText) maxLabel.textContent = highText;
    minRange.setAttribute('aria-valuetext', lowText);
    maxRange.setAttribute('aria-valuetext', highText);
    this.#morphObserver.takeRecords();
  }

  /**
   * Moves a thumb: keeps the pair ordered, writes the prices into the hidden
   * inputs (empty at either end, so no filter applies) and repaints. Results
   * update on release, through the component's `change` handler.
   * @param {Event} event - The input event from a range thumb
   */
  handleRangeInput(event) {
    const { minRange, maxRange, minInput, maxInput } = this.refs;
    if (!minRange || !maxRange || !minInput || !maxInput) return;

    const steps = Number(maxRange.max) || 1000;
    let from = Number(minRange.value);
    let to = Number(maxRange.value);

    if (from > to) {
      if (event.target === minRange) {
        from = to;
        minRange.value = String(from);
      } else {
        to = from;
        maxRange.value = String(to);
      }
    }

    const low = this.#positionToPrice(from, steps);
    const high = this.#positionToPrice(to, steps);
    minInput.value = from <= 0 ? '' : String(low);
    maxInput.value = to >= steps ? '' : String(high);
    this.#paintSlider(low, high);
  }

  /**
   * Sorts results by price ("Low to high" / "High to low" above the slider).
   * Same sort_by as the Sort control, so both stay in step after the
   * re-render; pressing the active order goes back to the default sort.
   * @param {MouseEvent} event - Click on a price-sort button
   */
  sortByPrice(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement) || !button.dataset.sort) return;

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const active = button.getAttribute('aria-pressed') === 'true';
    for (const sortButton of this.querySelectorAll('.price-facet__sort-button')) {
      sortButton.setAttribute('aria-pressed', String(!active && sortButton === button));
    }

    const url = new URL(window.location.href);
    if (active) {
      url.searchParams.delete('sort_by');
    } else {
      url.searchParams.set('sort_by', button.dataset.sort);
    }
    url.searchParams.delete('page');
    facetsForm.updateFiltersByURL(url.toString());
  }

  /**
   * Extracts the placeholder from a money format string, removing currency symbols.
   * @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
   * @returns {string} Just the placeholder (e.g., "{{amount}}")
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;
    // Slider thumbs need Home/End/PageUp/PageDown, which the filter below drops.
    if (event.target instanceof HTMLInputElement && event.target.type === 'range') return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);
    this.#applyPriceDefaults();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Parses a formatted money value into minor units
   * displayValue can come from user input or API response
   * @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
   * @param {string} currency - The currency code
   * @returns {number} The value in minor units
   */
  #parseDisplayValue(displayValue, currency) {
    return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const { currency, moneyFormat } = this;
    // Parse the user's input value using currency-aware parsing
    const value = this.#parseDisplayValue(input.value, currency);

    // data-min and data-max now contain raw minor unit values (not formatted)
    const min = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0', currency);
    const max = this.#parseDisplayValue(input.getAttribute('data-max') ?? '0', currency);

    if (value < min) {
      input.value = formatMoney(min, moneyFormat, currency);
    } else if (value > max) {
      input.value = formatMoney(max, moneyFormat, currency);
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      clearButton.classList.toggle(activeClass, event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        const span = document.createElement('span');
        span.className = 'visually-hidden';
        span.textContent = inputElement.getAttribute('aria-label') ?? '';
        return (swatch?.outerHTML ?? '') + span.outerHTML;
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.textContent = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);

    // Only a lower limit: "From …". The range max is the dearest 5 Kg pack,
    // not the 25 g ceiling the slider works in.
    const fromTemplate = facetStatus.dataset.fromTemplate;
    if (!maxInputValue && fromTemplate) {
      facetStatus.textContent = fromTemplate.replace('__PRICE__', this.#formatMoney(minInputNum));
      return;
    }

    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '') {
    // Try to parse the value
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    // Fall back to parsing the fallback string (which may have formatting like "11,400")
    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    // Last resort: clean and parse as integer
    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}
